/**
 * Baked custom-field lines on a Speaker / Sponsor card.
 *
 * MIRROR: `main-frontend/src/components/WebsiteBuilder/utils/CustomFieldLines.tsx`
 * renders the same markup on the editor canvas. The two repos share no package
 * for these child components, so a change here has to land there too —
 * otherwise the canvas and the live site disagree about a card's content and
 * height.
 *
 * The prop arrives as `unknown`: it comes from the saved `websiteJson`, which
 * is plain JSON with no type guarantee, and per-event runtime SNAPSHOTS mean a
 * page may be rendered by a bundle older or newer than the one that baked it.
 * Everything is validated defensively; a malformed entry is skipped rather than
 * thrown on, because a bad prop must never blank a published section.
 *
 * Values are already HTML-escaped at bake time and rendered as TEXT here.
 */

export type BakedCustomFieldLine = { label: string; value: string };

export function readCustomFieldLines(input: unknown): BakedCustomFieldLine[] {
  if (!Array.isArray(input)) return [];
  const out: BakedCustomFieldLine[] = [];
  for (const entry of input) {
    if (typeof entry !== "object" || entry === null) continue;
    const { label, value } = entry as Record<string, unknown>;
    if (typeof label !== "string" || typeof value !== "string") continue;
    if (!value.trim()) continue;
    out.push({ label, value });
  }
  return out;
}

export function CustomFieldLines({
  customFields,
  className,
}: {
  customFields?: unknown;
  className?: string;
}) {
  const lines = readCustomFieldLines(customFields);
  if (lines.length === 0) return null;
  return (
    <dl className={className ?? "mt-2 flex flex-col gap-1"}>
      {/* Index in the key: two definitions may carry the SAME label, and a
          label-only key would collide. Mirror of the editor twin. */}
      {lines.map((line, i) => (
        <div key={`${i}-${line.label}`} className="flex gap-1 text-sm">
          <dt className="opacity-70">{line.label}:</dt>
          <dd>{line.value}</dd>
        </div>
      ))}
    </dl>
  );
}
